import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMusic, IconPlayerPlay, IconTrash, IconUpload } from '@tabler/icons-react';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { slugify } from '../lib/slug';

type Song = {
  act: number;
  raw: string;
  nr: string;
  title: string;
  key: string;
};

type Recording = {
  id: string;
  voice: string;
  file_path: string;
  created_at: string;
};

const EMPTY_SONGS: Song[] = [];

function parseJsonList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((x) => String(x));
  return [];
}

function parseSongsFromConfig(list: string[], act: number): Song[] {
  const out: Song[] = [];
  for (const raw of list) {
    const txt = String(raw ?? '').trim();
    if (!txt) continue;
    const parts = txt.split(':');
    const nr = parts[0]?.trim() || txt;
    const title = (parts.length > 1 ? parts.slice(1).join(':') : txt).trim();
    const key = slugify(`${nr} ${title}`);
    out.push({ act, raw: txt, nr, title, key });
  }
  return out;
}

function publicChorUrl(path: string) {
  return supabase.storage.from('chor').getPublicUrl(path).data.publicUrl;
}

export function ChoirPage() {
  const qc = useQueryClient();
  const [songKey, setSongKey] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Chor Uploads – Musical!Kultur Admin';
  }, []);
  const [voice, setVoice] = useState('Sopran');
  const [file, setFile] = useState<File | null>(null);

  const cfgQ = useQuery({
    queryKey: ['config', 'ghost_musik'],
    queryFn: async (): Promise<Song[]> => {
      const { data, error } = await supabase
        .from('config')
        .select('key,value')
        .in('key', ['ghost_musik_akt1', 'ghost_musik_akt2']);
      if (error) throw error;

      const rows = (data ?? []) as Array<{ key: string; value: unknown }>;
      const byKey = new Map<string, unknown>();
      for (const row of rows) byKey.set(row.key, row.value);

      const a1 = parseJsonList(byKey.get('ghost_musik_akt1'));
      const a2 = parseJsonList(byKey.get('ghost_musik_akt2'));
      return [...parseSongsFromConfig(a1, 1), ...parseSongsFromConfig(a2, 2)];
    },
  });

  const songs = cfgQ.data ?? EMPTY_SONGS;

  const selectedSong = useMemo(
    () => songs.find((s) => s.key === songKey) ?? null,
    [songKey, songs],
  );

  const recQ = useQuery({
    queryKey: ['chor-recordings', songKey ?? 'none'],
    enabled: !!songKey,
    queryFn: async (): Promise<Recording[]> => {
      const { data, error } = await supabase
        .from('media_chor_files')
        .select('id,voice,file_path,created_at')
        .eq('song_key', songKey!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Recording[];
    },
  });

  const uploadM = useMutation({
    mutationFn: async () => {
      if (!selectedSong) throw new Error('Song fehlt');
      if (!file) throw new Error('Datei fehlt');
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!['mp3', 'm4a', 'wav'].includes(ext)) {
        throw new Error('Ungueltiger Dateityp (mp3/m4a/wav).');
      }

      const filename = `${selectedSong.key}-${Date.now()}.${ext}`;
      const path = `${selectedSong.key}/${filename}`;

      const { error: upErr } = await supabase.storage.from('chor').upload(path, file, {
        upsert: false,
        contentType: file.type || undefined,
        cacheControl: '3600',
      });
      if (upErr) throw upErr;

      const { error: metaErr } = await supabase.from('media_chor_files').insert({
        act: selectedSong.act,
        song_nr: selectedSong.nr,
        song_title: selectedSong.title,
        song_key: selectedSong.key,
        voice: voice.trim() || 'Unbekannt',
        file_path: path,
      });
      if (metaErr) throw metaErr;
    },
    onSuccess: async () => {
      setFile(null);
      notifications.show({ color: 'teal', title: 'Chor', message: 'Upload erfolgreich.' });
      await qc.invalidateQueries({ queryKey: ['chor-recordings'] });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Upload fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const deleteM = useMutation({
    mutationFn: async (rec: Recording) => {
      const ok = window.confirm('Aufnahme wirklich loeschen? (Datei + DB)');
      if (!ok) return;

      const { error: fileErr } = await supabase.storage.from('chor').remove([rec.file_path]);
      if (fileErr) throw fileErr;

      const { error: rowErr } = await supabase.from('media_chor_files').delete().eq('id', rec.id);
      if (rowErr) throw rowErr;
    },
    onSuccess: async () => {
      notifications.show({ color: 'teal', title: 'Chor', message: 'Geloescht.' });
      await qc.invalidateQueries({ queryKey: ['chor-recordings'] });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Loeschen fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const songOptions = useMemo(
    () =>
      songs.map((s) => ({
        value: s.key,
        label: `Akt ${s.act} · ${s.nr}: ${s.title}`,
      })),
    [songs],
  );

  return (
    <Stack gap="md">
      <PageHeader
        title="Choraufnahmen"
        description="Audio-Dateien zu Songs hochladen und verwalten."
        loading={cfgQ.isLoading}
      />

      <Card
        radius="lg"
        p="md"
        style={{
          background: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
        }}
      >
        <Stack gap="sm">
          <Select
            label="Song"
            placeholder="Song suchen…"
            data={songOptions}
            value={songKey}
            onChange={setSongKey}
            searchable
            clearable
            nothingFoundMessage="Kein Song"
          />

          <Group grow>
            <TextInput
              label="Stimme"
              value={voice}
              onChange={(e) => setVoice(e.currentTarget.value)}
              placeholder="Sopran/Alt/Tenor/Bass…"
            />

            <TextInput
              label="Datei (mp3/m4a/wav)"
              value={file?.name ?? ''}
              placeholder="Klicken und Datei auswaehlen…"
              readOnly
              onClick={async () => {
                const inp = document.createElement('input');
                inp.type = 'file';
                inp.accept = '.mp3,.m4a,.wav,audio/*';
                inp.onchange = () => {
                  const f = inp.files?.[0] ?? null;
                  setFile(f);
                };
                inp.click();
              }}
            />
          </Group>

          <Group justify="flex-end">
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={() => uploadM.mutate()}
              loading={uploadM.isPending}
              disabled={!selectedSong || !file}
            >
              Hochladen
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card
        radius="lg"
        p="md"
        style={{
          background: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
        }}
      >
        <Group justify="space-between" mb="sm">
          <Text c="var(--color-text)" fw={600}>
            Aufnahmen {selectedSong ? <Badge variant="light" color="brand">{selectedSong.nr}</Badge> : null}
          </Text>
          {recQ.isLoading && <Loader size="sm" />}
        </Group>

        {!songKey ? (
          <EmptyState
            icon={<IconMusic size={48} />}
            title="Kein Song ausgewählt"
            description="Wähle oben einen Song aus, um die vorhandenen Aufnahmen zu sehen."
          />
        ) : (recQ.data ?? []).length === 0 && !recQ.isLoading ? (
          <EmptyState
            icon={<IconMusic size={48} />}
            title="Noch keine Aufnahmen"
            description="Für diesen Song wurden noch keine Dateien hochgeladen."
          />
        ) : (
          <ScrollArea h={420}>
            <Table withRowBorders={false} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Voice</Table.Th>
                  <Table.Th>Erstellt</Table.Th>
                  <Table.Th>Link</Table.Th>
                  <Table.Th style={{ width: 70 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(recQ.data ?? []).map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text c="var(--color-text)">{r.voice}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text c="dimmed" size="sm">
                        {new Date(r.created_at).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="Oeffnen" withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="brand"
                            component="a"
                            href={publicChorUrl(r.file_path)}
                            target="_blank"
                          >
                            <IconPlayerPlay size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Text c="dimmed" size="xs">
                          {r.file_path}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Loeschen" withArrow>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => deleteM.mutate(r)}
                          loading={deleteM.isPending}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Stack>
  );
}
